<template>
  <div class="max-w-7xl mx-auto pb-12 space-y-8 animate-fade-in">

    <!-- ENCABEZADO Y FILTROS -->
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
          <PieChart class="w-6 h-6 text-brand-600" />
          Reporte de Cartera por Clasificación y Reservas
        </h1>
        <p class="text-slate-500 dark:text-slate-400 text-sm">
          Evaluación normativa de morosidad, rangos (A1-E) y detalle por tipo de amortización.
        </p>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <select
          v-model="selectedBranch"
          @change="loadReport"
          class="px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
        >
          <option value="">Todas las Sucursales</option>
          <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
        </select>

        <input
          v-model="targetDate"
          @change="loadReport"
          type="date"
          class="px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
        />

        <button
          @click="loadReport"
          :disabled="isLoading"
          class="p-2 bg-brand-50 text-white hover:bg-brand-100 rounded-xl transition-colors disabled:opacity-50"
          title="Actualizar reporte"
        >
          <RefreshCw class="w-4 h-4" :class="{ 'animate-spin': isLoading }" />
        </button>
      </div>
    </div>

    <!-- TARJETAS KPI -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <div class="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Cartera Activa Total</span>
        <div class="text-2xl font-mono font-black text-slate-900 dark:text-white">
          ${{ formatCurrency(kpis.total_portfolio) }}
        </div>
        <p class="text-xs text-slate-500 mt-1">{{ kpis.total_loans }} créditos vigentes</p>
      </div>

      <div class="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Reserva Requerida Total</span>
        <div class="text-2xl font-mono font-black text-amber-600 dark:text-amber-400">
          ${{ formatCurrency(kpis.total_provision) }}
        </div>
        <p class="text-xs text-slate-500 mt-1">
          {{ kpis.total_portfolio > 0 ? ((kpis.total_provision / kpis.total_portfolio) * 100).toFixed(2) : '0.00' }}% cobertura
        </p>
      </div>

      <div class="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Cartera en Riesgo (PAR > 30)</span>
        <div class="text-2xl font-mono font-black text-red-600 dark:text-red-400">
          ${{ formatCurrency(kpis.par_30_amount) }}
        </div>
        <p class="text-xs text-red-500 font-semibold mt-1">{{ kpis.par_30_percentage }}% del saldo total</p>
      </div>

      <div class="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Fecha de Evaluación</span>
        <div class="text-xl font-bold text-slate-800 dark:text-slate-200 mt-1">
          {{ formatDateDisplay(targetDate) }}
        </div>
        <p class="text-xs text-emerald-600 font-semibold mt-1 flex items-center gap-1">
          <CheckCircle2 class="w-3.5 h-3.5" /> Normativa aplicada
        </p>
      </div>
    </div>

    <!-- SECCIÓN 1: RESUMEN NORMATIVO POR CATEGORÍA -->
    <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30">
        <h3 class="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-tight">
          Resumen Consolidado por Categoría de Riesgo (A1 - E)
        </h3>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-xs text-left">
          <thead class="bg-slate-50 dark:bg-slate-800/60 text-slate-500 uppercase font-bold border-b border-slate-100 dark:border-slate-800">
            <tr>
              <th class="px-6 py-3.5">Categoría</th>
              <th class="px-6 py-3.5">Clasificación / Riesgo</th>
              <th class="px-6 py-3.5 text-center">No. Créditos</th>
              <th class="px-6 py-3.5 text-right">Saldo Capital</th>
              <th class="px-6 py-3.5 text-right">% Participación</th>
              <th class="px-6 py-3.5 text-right">Reserva Requerida ($)</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
            <tr v-if="isLoading">
              <td colspan="6" class="px-6 py-10 text-center text-slate-500">
                <Loader2 class="w-6 h-6 animate-spin mx-auto mb-2 text-brand-600" /> Calculando reporte normativo...
              </td>
            </tr>
            <tr v-else v-for="cat in summary" :key="cat.category" class="hover:bg-slate-50/70 dark:hover:bg-slate-800/50 transition-colors">
              <td class="px-6 py-3 font-mono font-black">
                <span :class="getBadgeClasses(cat.category)" class="px-2.5 py-1 rounded-lg text-xs border">
                  {{ cat.category }}
                </span>
              </td>
              <td class="px-6 py-3 font-semibold text-slate-700 dark:text-slate-300">{{ cat.label }}</td>
              <td class="px-6 py-3 text-center font-bold text-slate-600 dark:text-slate-400">{{ cat.count }}</td>
              <td class="px-6 py-3 text-right font-mono font-bold text-slate-900 dark:text-white">
                ${{ formatCurrency(cat.balance) }}
              </td>
              <td class="px-6 py-3 text-right font-mono text-slate-500">
                {{ kpis.total_portfolio > 0 ? ((cat.balance / kpis.total_portfolio) * 100).toFixed(2) : '0.00' }}%
              </td>
              <td class="px-6 py-3 text-right font-mono font-black text-amber-600 dark:text-amber-400">
                ${{ formatCurrency(cat.provision) }}
              </td>
            </tr>
          </tbody>
          <tfoot v-if="!isLoading" class="bg-slate-50 dark:bg-slate-800 font-bold border-t border-slate-200 dark:border-slate-700">
            <tr>
              <td colspan="2" class="px-6 py-3.5 uppercase text-slate-700 dark:text-slate-300">Totales Generales</td>
              <td class="px-6 py-3.5 text-center text-slate-900 dark:text-white">{{ kpis.total_loans }}</td>
              <td class="px-6 py-3.5 text-right font-mono text-slate-900 dark:text-white">${{ formatCurrency(kpis.total_portfolio) }}</td>
              <td class="px-6 py-3.5 text-right font-mono text-slate-700 dark:text-slate-300">100.00%</td>
              <td class="px-6 py-3.5 text-right font-mono text-amber-600 dark:text-amber-400">${{ formatCurrency(kpis.total_provision) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- SECCIÓN 2: DETALLE DE CARTERA (FLAT vs NORMAL) -->
    <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden space-y-4">

      <!-- HEADER CON PESTAÑAS FLAT vs NORMAL -->
      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-50/50 dark:bg-slate-800/30">

        <!-- Pestañas de separación de cartera -->
        <div class="flex bg-slate-200/60 dark:bg-slate-800 p-1 rounded-xl">
          <button
            @click="activeTab = 'NORMAL'"
            :class="activeTab === 'NORMAL'
              ? 'bg-white dark:bg-slate-900 text-brand-600 dark:text-white shadow-sm'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'"
            class="px-4 py-1.5 text-xs font-bold rounded-lg transition-all"
          >
            Cartera Normal / Sobre Saldo ({{ normalLoans.length }})
          </button>
          <button
            @click="activeTab = 'FLAT'"
            :class="activeTab === 'FLAT'
              ? 'bg-white dark:bg-slate-900 text-brand-600 dark:text-white shadow-sm'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'"
            class="px-4 py-1.5 text-xs font-bold rounded-lg transition-all"
          >
            Cartera Flat ({{ flatLoans.length }})
          </button>
        </div>

        <!-- Buscador -->
        <div class="relative w-full sm:w-64">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            v-model="searchTerm"
            type="text"
            placeholder="Buscar cliente, código, ref..."
            class="w-full pl-9 pr-4 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
        </div>
      </div>

      <!-- TABLA DETALLADA CON LOS 15 CAMPOS -->
      <div class="overflow-x-auto">
        <table class="w-full text-xs text-left whitespace-nowrap">
          <thead class="bg-slate-50 dark:bg-slate-800/60 text-slate-500 uppercase font-bold border-b border-slate-100 dark:border-slate-800">
            <tr>
              <th class="px-4 py-3">Cód. Cliente</th>
              <th class="px-4 py-3">Nombre del Cliente</th>
              <th class="px-4 py-3">No. Referencia (Exp)</th>
              <th class="px-4 py-3 text-right">Monto Desembolsado</th>
              <th class="px-4 py-3 text-center">F. Apertura</th>
              <th class="px-4 py-3 text-center">F. Vencimiento</th>
              <th class="px-4 py-3 text-center">Frecuencia</th>
              <th class="px-4 py-3 text-center">Plazo</th>
              <th class="px-4 py-3 text-right">Monto Cuota</th>
              <th class="px-4 py-3 text-right">Tasa Int.</th>
              <th class="px-4 py-3 text-right">Saldo Capital</th>
              <th class="px-4 py-3 text-right">Saldo Interés</th>
              <th class="px-4 py-3 text-right text-red-600">Saldo Cap. Mora</th>
              <th class="px-4 py-3 text-right text-red-600">Saldo Int. Mora</th>
              <th class="px-4 py-3 text-right font-black">Saldo Deuda Total</th>
              <th class="px-4 py-3 text-center">Cat. Riesgo</th>
              <th class="px-4 py-3 text-right">Reserva ($)</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
            <tr v-if="filteredLoans.length === 0">
              <td colspan="17" class="px-6 py-10 text-center text-slate-400">
                No hay créditos en esta cartera que coincidan con la búsqueda.
              </td>
            </tr>
            <tr v-else v-for="loan in filteredLoans" :key="loan.id" class="hover:bg-slate-50/70 dark:hover:bg-slate-800/50 transition-colors">
              <td class="px-4 py-3 font-mono font-bold text-slate-700 dark:text-slate-300">{{ loan.customer_code }}</td>
              <td class="px-4 py-3 font-bold text-slate-900 dark:text-white">{{ loan.customer_name }}</td>
              <td class="px-4 py-3 font-mono text-brand-600 dark:text-brand-400 font-bold">{{ loan.loan_code }}</td>
              <td class="px-4 py-3 text-right font-mono">${{ formatCurrency(loan.disbursed_amount) }}</td>
              <td class="px-4 py-3 text-center font-mono">{{ formatDateDisplay(loan.init_date) }}</td>
              <td class="px-4 py-3 text-center font-mono">{{ formatDateDisplay(loan.end_date) }}</td>
              <td class="px-4 py-3 text-center uppercase">{{ getFrequencyLabel(loan.condition_level) }}</td>
              <td class="px-4 py-3 text-center font-bold">{{ loan.term }} cuotas</td>
              <td class="px-4 py-3 text-right font-mono font-semibold">${{ formatCurrency(loan.installment_amount) }}</td>
              <td class="px-4 py-3 text-right font-mono">{{ loan.rate }}%</td>
              <td class="px-4 py-3 text-right font-mono font-bold text-slate-900 dark:text-white">${{ formatCurrency(loan.capital_balance) }}</td>
              <td class="px-4 py-3 text-right font-mono text-amber-600 dark:text-amber-400">${{ formatCurrency(loan.interest_balance) }}</td>
              <td class="px-4 py-3 text-right font-mono font-bold text-red-600 dark:text-red-400">${{ formatCurrency(loan.capital_mora_balance) }}</td>
              <td class="px-4 py-3 text-right font-mono font-bold text-red-600 dark:text-red-400">${{ formatCurrency(loan.interest_mora_balance) }}</td>
              <td class="px-4 py-3 text-right font-mono font-black text-slate-900 dark:text-white">${{ formatCurrency(loan.total_debt_balance) }}</td>
              <td class="px-4 py-3 text-center">
                <span :class="getBadgeClasses(loan.risk_category)" class="px-2 py-0.5 rounded-md text-[10px] font-black border">
                  {{ loan.risk_category }}
                </span>
              </td>
              <td class="px-4 py-3 text-right font-mono font-bold text-amber-600 dark:text-amber-400">
                ${{ formatCurrency(loan.provision_amount) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { PieChart, RefreshCw, Search, Loader2, CheckCircle2 } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';

const { showError } = useToast();

const isLoading = ref(true);
const activeTab = ref('NORMAL');
const branches = ref([]);
const selectedBranch = ref('');
const targetDate = ref('');
const searchTerm = ref('');

const summary = ref([]);
const loansList = ref([]);
const kpis = ref({
  total_portfolio: 0,
  total_provision: 0,
  total_loans: 0,
  par_30_amount: 0,
  par_30_percentage: 0
});

const formatCurrency = (val) => {
  return Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split(' ')[0].split('-');
  return `${day}/${month}/${year}`;
};

const getFrequencyLabel = (level) => {
  const map = { 1: 'Diario', 2: 'Semanal', 3: 'Quincenal', 4: 'Mensual' };
  return map[Number(level)] || 'Mensual';
};

const getBadgeClasses = (cat) => {
  const map = {
    'A1': 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800',
    'A2': 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-950/40 dark:text-teal-300 dark:border-teal-800',
    'B':  'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800',
    'C1': 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-800',
    'C2': 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/50 dark:text-orange-200 dark:border-orange-700',
    'D1': 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800',
    'D2': 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/50 dark:text-red-200 dark:border-red-700',
    'E':  'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800',
  };
  return map[cat] || 'bg-slate-100 text-slate-700 border-slate-200';
};

const fetchBranches = async () => {
  const user = SecureStorage.getItem('llevo_user');
  if (!user?.company) return;
  try {
    const res = await apiClient.get(`/branches?company=${user.company}`);
    branches.value = Array.isArray(res.data) ? res.data : (res.data.data || []);
  } catch (e) {
    console.error('Error cargando sucursales:', e);
  }
};

const loadReport = async () => {
  isLoading.value = true;
  try {
    const user = SecureStorage.getItem('llevo_user');
    if (!user?.company) return;

    const params = new URLSearchParams({
      company_id: user.company,
      date: targetDate.value
    });

    if (selectedBranch.value) {
      params.append('branch_id', selectedBranch.value);
    }

    const res = await apiClient.get(`/portfolio_risk_report?${params.toString()}`);
    if (res.data && res.data.success) {
      kpis.value = res.data.kpis;
      summary.value = res.data.summary;
      loansList.value = res.data.data;
    }
  } catch (e) {
    console.error(e);
    showError('No se pudo generar el reporte de cartera.');
  } finally {
    isLoading.value = false;
  }
};

const normalLoans = computed(() => loansList.value.filter(l => !l.is_flat));
const flatLoans = computed(() => loansList.value.filter(l => l.is_flat));

const filteredLoans = computed(() => {
  let result = activeTab.value === 'NORMAL' ? normalLoans.value : flatLoans.value;

  if (searchTerm.value) {
    const q = searchTerm.value.toLowerCase();
    result = result.filter(l =>
      l.customer_name?.toLowerCase().includes(q) ||
      l.loan_code?.toLowerCase().includes(q) ||
      l.customer_code?.toLowerCase().includes(q)
    );
  }

  return result;
});

onMounted(() => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  targetDate.value = `${year}-${month}-${day}`;

  fetchBranches();
  loadReport();
});
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in { animation: fade-in 0.25s ease-out forwards; }
</style>